import {
  createTranslateFactory,
  isRightToLeft,
  type Translate,
} from "../../../../packages/i18n/src";
import en from "../../assets/locales/en.json";
import es from "../../assets/locales/es.json";
import fr from "../../assets/locales/fr.json";
import ja from "../../assets/locales/ja.json";
import ar from "../../assets/locales/ar.json";
import zhCn from "../../assets/locales/zh-CN.json";
import ptBr from "../../assets/locales/pt-BR.json";
import de from "../../assets/locales/de.json";
import ru from "../../assets/locales/ru.json";
import ko from "../../assets/locales/ko.json";
import hi from "../../assets/locales/hi.json";
import id from "../../assets/locales/id.json";
import tr from "../../assets/locales/tr.json";
import vi from "../../assets/locales/vi.json";
import th from "../../assets/locales/th.json";
import fi from "../../assets/locales/fi.json";
import pl from "../../assets/locales/pl.json";
import sv from "../../assets/locales/sv.json";
import nl from "../../assets/locales/nl.json";
import it from "../../assets/locales/it.json";

export type { Translate } from "../../../../packages/i18n/src";

type LocaleListener = (translate: Translate) => void;

type Translations = Record<string, Record<string, string>>;

const translations: Translations = {
  en,
  es,
  fr,
  ja,
  ar,
  "zh-CN": zhCn,
  "pt-BR": ptBr,
  de,
  ru,
  ko,
  hi,
  id,
  tr,
  vi,
  th,
  fi,
  pl,
  sv,
  nl,
  it,
};

const DEFAULT_LOCALE = "en";

const createWavemakerTranslate = createTranslateFactory({
  translations,
  defaultLocale: DEFAULT_LOCALE,
});

let currentTranslate: Translate = createWavemakerTranslate(DEFAULT_LOCALE);
const listeners = new Set<LocaleListener>();
let directionRoot: HTMLElement | null = null;

const parseVars = (raw?: string): Record<string, unknown> | undefined => {
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return undefined;
  }
  return undefined;
};

const parseAttributeMap = (raw?: string): Array<{ attr: string; key: string }> => {
  if (!raw) {
    return [];
  }
  return raw
    .split(";")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [attr, key] = entry.split("=");
      return { attr: attr?.trim() ?? "", key: key?.trim() ?? "" };
    })
    .filter((entry) => entry.attr && entry.key);
};

const serializeAttributeMap = (entries: Record<string, string>): string =>
  Object.entries(entries)
    .filter(([, value]) => value)
    .map(([attr, key]) => `${attr}=${key}`)
    .join(";");

const applyTranslationToElement = (element: HTMLElement, translate: Translate): void => {
  const key = element.dataset.i18n;
  if (!key) {
    return;
  }
  const vars = parseVars(element.dataset.i18nVars);
  element.textContent = translate.t(key, vars);
};

const applyAttributeTranslations = (element: HTMLElement, translate: Translate): void => {
  const entries = parseAttributeMap(element.dataset.i18nAttr);
  entries.forEach(({ attr, key }) => {
    element.setAttribute(attr, translate.t(key));
  });
};

const applyDirection = (locale: string): void => {
  if (typeof document === "undefined") {
    return;
  }
  const root = directionRoot ?? document.body;
  if (!root) {
    return;
  }
  root.setAttribute("dir", isRightToLeft(locale) ? "rtl" : "ltr");
};

export const setDirectionRoot = (root?: HTMLElement | null): void => {
  directionRoot = root ?? null;
  applyDirection(currentTranslate.lang);
};

export const createTranslate = (locale?: string): Translate =>
  createWavemakerTranslate(locale);

export const getTranslate = (): Translate => currentTranslate;

export const onLocaleChange = (listener: LocaleListener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const setTranslate = (translate: Translate): void => {
  currentTranslate = translate;
  applyTranslations();
  applyDirection(translate.lang);
  listeners.forEach((listener) => listener(translate));
};

export const setLocale = (locale?: string): Translate => {
  const next = createWavemakerTranslate(locale);
  setTranslate(next);
  return next;
};

export const setLocalizedText = (
  element: HTMLElement,
  key: string,
  vars?: Record<string, unknown>
): void => {
  element.dataset.i18n = key;
  if (vars && Object.keys(vars).length) {
    element.dataset.i18nVars = JSON.stringify(vars);
  } else {
    delete element.dataset.i18nVars;
  }
  applyTranslationToElement(element, currentTranslate);
};

export const setLocalizedAttribute = (
  element: HTMLElement,
  attr: string,
  key: string
): void => {
  const entries = parseAttributeMap(element.dataset.i18nAttr);
  const merged: Record<string, string> = {};
  entries.forEach(({ attr: existingAttr, key: existingKey }) => {
    merged[existingAttr] = existingKey;
  });
  merged[attr] = key;
  element.dataset.i18nAttr = serializeAttributeMap(merged);
  applyAttributeTranslations(element, currentTranslate);
};

export const applyTranslations = (root?: ParentNode): void => {
  if (typeof document === "undefined") {
    return;
  }
  const translate = currentTranslate;
  const scope: ParentNode = root ?? document;
  if (scope instanceof HTMLElement) {
    applyTranslationToElement(scope, translate);
    applyAttributeTranslations(scope, translate);
  }
  scope
    .querySelectorAll<HTMLElement>("[data-i18n]")
    .forEach((element) => applyTranslationToElement(element, translate));
  scope
    .querySelectorAll<HTMLElement>("[data-i18n-attr]")
    .forEach((element) => applyAttributeTranslations(element, translate));
};

export const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
  const locale = currentTranslate.lang || DEFAULT_LOCALE;
  try {
    return new Intl.NumberFormat(locale, options).format(value);
  } catch {
    return String(value);
  }
};

export const getAvailableLocales = (): string[] => Object.keys(translations);
